import React from 'react';
import {
  AppBar as MuiAppBar,
  Toolbar,
  IconButton,
  Typography,
  Stack,
  Box,
  Chip,
  alpha,
} from '@mui/material';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { useWarbandStore } from '../../stores/warbandStore';
import { SpellSchoolChip } from '../shared/SpellSchoolChip';
import { useReferenceDataStore } from '../../stores/referenceDataStore';
import { recessedText } from '../../theme/surfaces';
import { PageNavigationMenu } from './PageNavigationMenu';
import { treasureEffect } from '../../App';
import { ManageGoldModal } from '../modals/ManageGoldModal';
import { ManageLevelsModal } from '../modals/ManageLevelsModal';
import { getWizardLevelDescription, computeUnusedLevelUps } from '../../utils/formatting';
import { useGameStore } from '../../stores/gameStore';
import { holoBanded, TERMINAL_GREEN } from '../../theme/stargraveTheme';

interface AppBarProps {
  title?: string;
}

export const AppBar: React.FC<AppBarProps> = ({ title = 'Mini Fridge' }) => {
  const { toggleMenu, selectedWarbandId, warbands, currentPage } = useWarbandStore();
  const { getSchoolNameById } = useReferenceDataStore();
  const activeGameSystem = useGameStore((state) => state.activeGameSystem);
  const levelUps = useWarbandStore((s) => s.levelUps);
  const [goldModalOpen, setGoldModalOpen] = React.useState(false);
  const [experienceModalOpen, setExperienceModalOpen] = React.useState(false);

  const selectedWarband = selectedWarbandId
    ? warbands.find((w) => w.id === selectedWarbandId)
    : null;

  const unusedLevelUps = selectedWarband ? computeUnusedLevelUps(selectedWarband, levelUps) : 0;

  const displayTitle = selectedWarband ? selectedWarband.name : title;

  return (
    <>
      <MuiAppBar
        position="static"
        sx={{
          zIndex: (theme) => theme.zIndex.appBar + 1,
        }}
      >
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleMenu}
            sx={{
              mr: 2,
              color: recessedText.color,
              textShadow: recessedText.textShadow,
              '& img': {
                filter: 'drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.6))',
                transition: 'filter 0.1s ease',
              },
              '&:active img': {
                filter: 'drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.3))',
              },
            }}
          >
            {activeGameSystem === 'frostgrave'
              ? <img src="/bigf.png" alt="menu" style={{ width: 48, height: 48 }} />
              : <Box component="span" aria-hidden sx={{
                  ...holoBanded,
                  width: 48,
                  height: 48,
                  display: 'grid',
                  placeItems: 'center',
                  fontSize: 42,
                  fontWeight: 800,
                  lineHeight: 1,
                  fontFamily: '"Consolas", "Courier New", monospace',
                  // Simple theme strips the banded text fill, so the glyph inherits the navy IconButton colour
                  'body.simple-mode &': { color: TERMINAL_GREEN },
                }}>S</Box>}
          </IconButton>
          <Stack sx={{ flexGrow: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
              <Typography variant="h6" component="div">
                {displayTitle}
              </Typography>
              <PageNavigationMenu />
            </Stack>
            {selectedWarband != null && activeGameSystem === 'frostgrave' && (
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SpellSchoolChip
                    label={getWizardLevelDescription(selectedWarband) + ' ' + (getSchoolNameById(selectedWarband?.schoolId) ?? 'Unknown School')}
                    onClick={() => setExperienceModalOpen(true)}
                  />
                  {(unusedLevelUps ?? 0) > 0 && (
                    <Chip
                      size="small"
                      label={`${unusedLevelUps}`}
                      icon={<FileUploadIcon fontSize="small" />}
                      sx={{
                        backgroundColor: alpha("#FFD700", 0.55),
                        ":hover": {
                          backgroundColor: alpha("#FFD700", 0.75),
                        },
                      }}
                    />
                  )}

                  {currentPage === 'manage' && (
                    <Chip
                      label={`${selectedWarband.gold} gc`}
                      size="small"
                      onClick={() => setGoldModalOpen(true)}
                      sx={{ ...treasureEffect, color: '#EDD270', cursor: 'pointer' }}
                    />
                  )}
                </Box>
              </Stack>
            )}
            {/* Page navigation dropdown for warband pages */}
          </Stack>
        </Toolbar>
      </MuiAppBar>
      {selectedWarband && (
        <ManageGoldModal
          open={goldModalOpen}
          warband={selectedWarband}
          onClose={() => setGoldModalOpen(false)}
        />
      )}
      {selectedWarband && (
        <ManageLevelsModal
          open={experienceModalOpen}
          warband={selectedWarband}
          onClose={() => setExperienceModalOpen(false)}
        />
      )}
    </>
  );
};
